import { useEffect, type RefObject } from 'react'

/**
 * How much smaller the visual viewport must get (vs window.innerHeight) while
 * an editable is focused before we call it "the on-screen keyboard is open"
 * rather than a URL-bar collapse or a pinch-zoom (~60-80px).
 */
const KEYBOARD_THRESHOLD_PX = 120

/**
 * Pin an element's height to the visual viewport while the on-screen keyboard
 * is open, so a bottom composer stays above the keys (iOS — Android already
 * resizes the layout viewport via the `interactive-widget=resizes-content`
 * viewport meta, which iOS ignores).
 *
 * Attach it to the keyboard-affected container:
 * - a `fixed inset-x-0 top-<header> bottom-0` mobile sheet (inline height
 *   wins: CSS drops `bottom` when top+height+bottom are all constrained);
 * - the app shell root (`h-[100dvh]`), which fixes every in-flow page at once.
 *
 * Mechanics: while `document.activeElement` is an editable AND the viewport
 * shrank by more than the threshold, set `el.style.height` to the visible
 * bottom edge (visual viewport height plus its `offsetTop` pan, minus the
 * element's own top — rects are layout-viewport-relative, and iOS pans the
 * layout viewport under the keyboard). When the keyboard closes, the inline
 * height is cleared so the class-driven sizing takes over again.
 *
 * Inert without `window.visualViewport` (old Safari, jsdom → tests), without
 * `enabled`, and on desktop (zoom moves innerHeight and vv.height together).
 */
export function useVisualViewportHeight(
  ref: RefObject<HTMLElement | null>,
  enabled: boolean,
  opts?: { threshold?: number },
): void {
  const threshold = opts?.threshold ?? KEYBOARD_THRESHOLD_PX

  useEffect(() => {
    if (!enabled) return
    const vv = typeof window !== 'undefined' ? window.visualViewport : null
    const el = ref.current
    if (!vv || !el) return

    // Last height written (-1 = none) — damps the iOS pan→reflow→re-pan
    // jitter and lets cleanup/reset distinguish "cleared" from "cleared again".
    let appliedHeight = -1

    const isEditableFocused = (): boolean => {
      const a = document.activeElement
      if (!a) return false
      return (
        a.tagName === 'INPUT' ||
        a.tagName === 'TEXTAREA' ||
        (a as HTMLElement).isContentEditable
      )
    }

    const update = () => {
      if (isEditableFocused() && window.innerHeight - vv.height > threshold) {
        const elTop = Math.max(el.getBoundingClientRect().top, 0)
        const height = Math.round(vv.offsetTop + vv.height - elTop)
        if (Math.abs(height - appliedHeight) > 1) {
          appliedHeight = height
          el.style.height = `${height}px`
        }
      } else if (appliedHeight !== -1) {
        appliedHeight = -1
        el.style.height = ''
      }
    }

    // focusin/focusout re-run the gate when focus moves between editables
    // (the keyboard may stay open with a different size, e.g. the quickType
    // bar collapsing fires a plain resize — covered too).
    update()
    vv.addEventListener('resize', update)
    vv.addEventListener('scroll', update)
    document.addEventListener('focusin', update)
    document.addEventListener('focusout', update)
    return () => {
      vv.removeEventListener('resize', update)
      vv.removeEventListener('scroll', update)
      document.removeEventListener('focusin', update)
      document.removeEventListener('focusout', update)
      el.style.height = ''
    }
  }, [ref, enabled, threshold])
}
